/* Site chrome: transparent homepage header and fitted wordmark. */
(() => {
  if (window.skinChromeLoaded) return;
  window.skinChromeLoaded = true;

  /* Transparent header over the homepage hero */
  function initOverlayHeader() {
    const wrapper = document.querySelector('.skin-header--overlay');
    if (!wrapper) return;
    const section = wrapper.closest('.section-header');
    const firstMain = document.querySelector('main .shopify-section');
    if (!section || !firstMain || !firstMain.querySelector('[data-skin-hero]')) return;

    section.classList.add('skin-overlay-active');
    const setOffset = () => section.style.setProperty('--skin-header-offset', `${wrapper.offsetHeight}px`);
    const setSolid = () => wrapper.classList.toggle('is-solid', window.scrollY > 24);
    setOffset();
    setSolid();
    new ResizeObserver(setOffset).observe(wrapper);
    window.addEventListener('scroll', setSolid, { passive: true });
  }

  /* Scale wordmark text to fill its container width */
  function fitWordmarks() {
    document.querySelectorAll('[data-skin-fit]').forEach((el) => {
      const parent = el.parentElement;
      const style = getComputedStyle(parent);
      const available = parent.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      el.style.fontSize = '100px';
      el.style.display = 'inline-block';
      const ratio = available / el.scrollWidth;
      el.style.display = '';
      el.style.fontSize = `${Math.floor(100 * ratio * 0.98)}px`;
      el.classList.add('is-fitted');
    });
  }

  const init = () => {
    initOverlayHeader();
    fitWordmarks();
    if (document.fonts) document.fonts.ready.then(fitWordmarks);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(fitWordmarks, 150);
  });
  document.addEventListener('shopify:section:load', init);

  /* Footer link lists open and close on mobile */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-skin-footer-toggle]');
    if (!toggle) return;
    const block = toggle.closest('.footer-block');
    const open = !block.classList.contains('is-open');
    block.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
  });

  /* Logos: crop the transparent margins baked into uploaded logo files so the
     visible logo fills exactly the width set in the theme editor. The crop is
     measured once per file and saved; snippets/skin-logo-trim.liquid re-applies
     it before the first paint on every later page view. */
  const logoTrim = window.skinLogoTrim;

  function trimLogo(img) {
    const state = img.dataset.skinTrim;
    if (state && state !== 'pending') return;
    const saved = logoTrim.read(img);
    if (saved) {
      logoTrim.apply(img, saved);
      window.dispatchEvent(new Event('resize'));
      return;
    }
    img.dataset.skinTrim = 'measuring';
    const source = new Image();
    source.crossOrigin = 'anonymous';
    source.onload = () => {
      try {
        const scale = Math.min(1, 800 / source.naturalWidth);
        const w = Math.max(1, Math.round(source.naturalWidth * scale));
        const h = Math.max(1, Math.round(source.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(source, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        let minX = w, minY = h, maxX = -1, maxY = -1;
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            if (data[(y * w + x) * 4 + 3] > 2) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }
        if (maxX < 0) {
          img.dataset.skinTrim = 'skipped';
          return;
        }
        // Keep a small safety margin so soft edges (shadows, drops, anti-aliasing) are never clipped
        const pad = Math.max(2, Math.round(Math.max(maxX - minX, maxY - minY) * 0.02));
        minX = Math.max(0, minX - pad);
        minY = Math.max(0, minY - pad);
        maxX = Math.min(w - 1, maxX + pad);
        maxY = Math.min(h - 1, maxY + pad);
        const fw = (maxX - minX + 1) / w, fh = (maxY - minY + 1) / h;
        const trim =
          fw > 0.97 && fh > 0.97
            ? { none: true }
            : {
                x: +(minX / w).toFixed(5),
                y: +(minY / h).toFixed(5),
                w: +fw.toFixed(5),
                h: +fh.toFixed(5),
                ratio: +((fw * source.naturalWidth) / (fh * source.naturalHeight)).toFixed(4),
              };
        logoTrim.write(img, trim);
        logoTrim.apply(img, trim);
        window.dispatchEvent(new Event('resize'));
      } catch (e) {
        img.dataset.skinTrim = 'skipped';
      }
    };
    source.onerror = () => {
      img.dataset.skinTrim = 'skipped';
    };
    source.src = img.currentSrc || img.src;
  }

  function trimLogos(root = document) {
    if (!logoTrim) return;
    root.querySelectorAll(logoTrim.selector).forEach((img) => {
      if (logoTrim.read(img)) trimLogo(img);
      else if (img.complete && img.naturalWidth) trimLogo(img);
      else img.addEventListener('load', () => trimLogo(img), { once: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => trimLogos());
  } else {
    trimLogos();
  }
  document.addEventListener('shopify:section:load', (event) => trimLogos(event.target));

  /* Bag "Make it 2" offer (snippets/skin-cart-upgrade.liquid): sets the line to 2 through the
     bag's own quantity update, so the drawer or cart page re-renders with the discount applied. */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-skin-upgrade]');
    if (!button) return;
    const cartItems = button.closest('cart-drawer-items, cart-items');
    if (!cartItems?.updateQuantity) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    // updateQuantity compares the cart's answer with the line's input, so the input has to say 2
    // first, or it reports a false "You can only add 2 of this item" error
    const index = button.dataset.index;
    document.querySelectorAll(`#Drawer-quantity-${index}, #Quantity-${index}`).forEach((input) => {
      input.value = 2;
    });
    cartItems.updateQuantity(index, 2, event, 'plus', button.dataset.variantId);
    window.Shopify?.analytics?.publish?.('dewlab:upgrade_to_2', {
      productId: button.dataset.productId,
      variantId: button.dataset.variantId,
    });
  });
})();
